/**
 * ContentForge — Settings View
 *
 * Per-project settings: site info, WordPress connection,
 * publishing schedule, and editable agent prompts.
 */

import { html, nothing, type TemplateResult } from "lit";
import type { ContentForgeDashboardData } from "../controllers/contentforge.ts";
import type { GatewayBrowserClient } from "../gateway.ts";

// ─── Types ──────────────────────────────────────────────────────────────

type SiteData = {
  id: string;
  url: string;
  name: string;
  wpApiUrl: string;
  wpUsername: string;
  wpAppPassword: string;
  sitemapUrl: string;
  publishSchedule: {
    postsPerDay: number;
    draftsPerDay: number;
    timeWindows: string[];
    articlesPerWeek?: { min: number; max: number };
  };
  contentMix?: { informationalShare?: number; maxProductsPerArticle?: number };
  strategistPrompt?: string;
  writerPrompt?: string;
  linkerPrompt?: string;
  artistPrompt?: string;
  arthurPrompt?: string;
  minerPrompt?: string;
  // Google / Bing integration
  gscSiteUrl?: string;
  gscMinerEnabled?: boolean;
  bingSiteUrl?: string;
  bingMinerEnabled?: boolean;
  geoTarget?: number;
  languageCode?: number;
  siteLang?: string;
  minerExcludeTerms?: string;
  aiQualityWall?: boolean;
  siteNiche?: string;
  hitlPhase?: 1 | 2 | 3;
  productsRequired?: boolean;
  injectContextualProducts?: boolean;
  publishMode?: "wordpress" | "static_html";
  blogPublishKey?: string;
  monthlyBudgetUsd?: number;
  amazonSource?: {
    tag: string;
    domain: string;
    categories: { nodeId: string; label: string }[];
    minPrice?: number;
    minRating?: number;
    minReviews?: number;
    minerEnabled?: boolean;
    sourcerEnabled?: boolean;
    usedAsins?: string[];
  };
  pinterest?: {
    enabled?: boolean;
    appId?: string;
    appSecret?: string;
    accessToken?: string;
    refreshToken?: string;
    tokenExpiresAt?: number;
    dailyPinCap?: number;
    activeHours?: [number, number];
    variantsPerArticle?: number;
    boardCreatesPerDay?: number;
    articleRepinGapDays?: number;
    brandLabel?: string;
    templateWeights?: Record<string, number>;
  };
};

type DefaultPrompts = {
  strategistPrompt: string;
  writerPrompt: string;
  linkerPrompt: string;
  artistPrompt: string;
  arthurPrompt: string;
  minerPrompt: string;
  publishSchedule: { postsPerDay: number; draftsPerDay: number; timeWindows: string[] };
};

type SettingsState = {
  site: SiteData | null;
  defaults: DefaultPrompts | null;
  loading: boolean;
  saving: boolean;
  saved: boolean;
  error: string | null;
  wpTestResult: string | null;
  wpTesting: boolean;
  activeSection: string;
  // Product sources
  csvSources: Array<{
    id: string;
    filename: string;
    productCount: number;
    active: boolean;
    mineKeywords?: boolean;
    affCode?: string;
    merchantId?: string;
    trackingRef?: string;
  }>;
  csvSourcesLoaded: boolean;
  webSources: Array<{
    id: string;
    name: string;
    sitemapUrl: string;
    awinMid: string;
    awinAffId: string;
    active: boolean;
    productCount: number;
    lastScrapedAt: number;
  }>;
  webSourcesLoaded: boolean;
  addingWebSource: boolean;
  scrapingSourceId: string | null;
  miningInProgress: boolean;
  minerResult: {
    phase: string;
    totalGenerated: number;
    totalAfterCluster: number;
    totalAdded: number;
    gscEasyWins: number;
    durationMs: number;
    allProductsMined?: boolean;
  } | null;
  // Per-category Amazon mining
  miningCategoryNodeId: string | null;
  categoryMinerResult: {
    categoryLabel: string;
    totalGenerated: number;
    totalAdded: number;
    durationMs: number;
  } | null;
};

// ─── Module State ───────────────────────────────────────────────────────

let _state: SettingsState = {
  site: null,
  defaults: null,
  loading: true,
  saving: false,
  saved: false,
  error: null,
  wpTestResult: null,
  wpTesting: false,
  activeSection: "site",
  csvSources: [],
  csvSourcesLoaded: false,
  webSources: [],
  webSourcesLoaded: false,
  addingWebSource: false,
  scrapingSourceId: null,
  miningInProgress: false,
  minerResult: null,
  miningCategoryNodeId: null,
  categoryMinerResult: null,
};

let _client: GatewayBrowserClient | null = null;
let _onRefresh: (() => void) | null = null;
let _onProjectCreated: ((siteId: string) => void) | null = null;

// Article refresh state (Amazon tab)
let _articleRefreshing = false;
let _articleRefreshResult: {
  success: boolean;
  oldProducts: string[];
  newProducts: string[];
  cardsReplaced: number;
  tableReplaced: boolean;
  paragraphsRewritten: number;
  details: string[];
} | null = null;
let _articleRefreshError: string | null = null;

// Fix broken products state (Amazon tab)
let _fixBrokenRunning = false;
let _fixBrokenResult: {
  fixed: number;
  skipped: number;
  errors: number;
  details: string[];
} | null = null;
let _fixBrokenError: string | null = null;
let _initialized = false;
let _createMode = false;
let _loadForSiteId: string | null = null;

// ─── Data Loading ───────────────────────────────────────────────────────

async function loadSettingsData(
  client: GatewayBrowserClient,
  _dashboard: ContentForgeDashboardData | null,
  activeSiteId?: string | null,
) {
  _state = { ..._state, loading: true, error: null, wpTestResult: null, saved: false };
  _onRefresh?.();

  try {
    // Load defaults
    const defaults = await client.request<DefaultPrompts>("contentforge.sites.getDefaults", {});
    _state.defaults = defaults;

    if (_createMode) {
      // Create mode: show blank form
      _state.site = {
        id: "",
        url: "",
        name: "",
        wpApiUrl: "",
        wpUsername: "",
        wpAppPassword: "",
        sitemapUrl: "",
        publishSchedule: {
          postsPerDay: 2,
          draftsPerDay: 2,
          timeWindows: ["09:00-12:00", "14:00-18:00"],
        },
      } as SiteData;
    } else {
      // Edit mode: load the active site or first available
      const sitesRes = await client.request<{ sites: SiteData[] }>("contentforge.sites.list", {});
      const sites = sitesRes?.sites ?? [];
      const targetId = activeSiteId || sites[0]?.id;
      if (targetId) {
        const site = await client.request<SiteData>("contentforge.sites.get", { id: targetId });
        _state.site = site;
      }
    }

    _state.loading = false;
  } catch (err) {
    _state.error = (err as Error).message;
    _state.loading = false;
  }
  _onRefresh?.();
}

async function saveSiteSettings() {
  if (!_client) {
    return;
  }
  _state = { ..._state, saving: true, saved: false, error: null };
  _onRefresh?.();

  try {
    // Collect form values
    const form = document.getElementById("cf-settings-form") as HTMLFormElement | null;
    if (!form) {
      throw new Error("Form not found");
    }

    // Only include fields whose input elements exist in the DOM
    // (only the active tab's inputs are rendered)
    const getIfExists = (id: string) => {
      const el = form.querySelector(`#${id}`);
      return el ? el.value : undefined;
    };
    const getTaIfExists = (id: string) => {
      const el = form.querySelector(`#${id}`);
      return el ? el.value : undefined;
    };

    const patch: Record<string, unknown> = {};

    // Site Info tab fields
    const name = getIfExists("cfg-name");
    if (name !== undefined) {
      patch.name = name;
    }
    const url = getIfExists("cfg-url");
    if (url !== undefined) {
      patch.url = url;
    }
    const sitemapUrl = getIfExists("cfg-sitemap");
    if (sitemapUrl !== undefined) {
      patch.sitemapUrl = sitemapUrl;
    }
    // Collect additional sitemaps from dynamic inputs
    const extraInputs = document.querySelectorAll<HTMLInputElement>(".cfg-sitemap-extra-input");
    const extraUrls = [...extraInputs].map((el) => el.value.trim()).filter((v) => v.length > 0);
    patch.sitemapUrls = extraUrls.length > 0 ? extraUrls : undefined;
    const gscUrl = getIfExists("cfg-gsc-url");
    if (gscUrl !== undefined) {
      patch.gscSiteUrl = gscUrl || undefined;
    }
    const gscMinerEl = document.getElementById("cfg-gsc-miner") as HTMLInputElement | null;
    if (gscMinerEl) {
      patch.gscMinerEnabled = gscMinerEl.checked;
    }
    const bingUrl = getIfExists("cfg-bing-url");
    if (bingUrl !== undefined) {
      patch.bingSiteUrl = bingUrl || undefined;
    }
    const bingMinerEl = document.getElementById("cfg-bing-miner") as HTMLInputElement | null;
    if (bingMinerEl) {
      patch.bingMinerEnabled = bingMinerEl.checked;
    }
    const geoTarget = getIfExists("cfg-geo-target");
    if (geoTarget !== undefined) {
      patch.geoTarget = Number.parseInt(geoTarget, 10) || undefined;
    }
    const langCode = getIfExists("cfg-lang-code");
    if (langCode !== undefined) {
      patch.languageCode = Number.parseInt(langCode, 10) || undefined;
    }
    const siteLang = getIfExists("cfg-site-lang");
    if (siteLang !== undefined) {
      patch.siteLang = siteLang || undefined;
    }
    const minerExcludeTerms = getIfExists("cfg-miner-exclude");
    if (minerExcludeTerms !== undefined) {
      patch.minerExcludeTerms = minerExcludeTerms || undefined;
    }
    const aiQualityWallEl = document.getElementById(
      "cfg-ai-quality-wall",
    ) as HTMLInputElement | null;
    if (aiQualityWallEl) {
      patch.aiQualityWall = aiQualityWallEl.checked;
    }
    const siteNiche = getIfExists("cfg-site-niche");
    if (siteNiche !== undefined) {
      patch.siteNiche = siteNiche || undefined;
    }

    // WordPress tab fields
    const wpApiUrl = getIfExists("cfg-wp-api");
    if (wpApiUrl !== undefined) {
      patch.wpApiUrl = wpApiUrl;
    }
    const wpUsername = getIfExists("cfg-wp-user");
    if (wpUsername !== undefined) {
      patch.wpUsername = wpUsername;
    }
    const wpAppPassword = getIfExists("cfg-wp-pass");
    if (wpAppPassword !== undefined) {
      patch.wpAppPassword = wpAppPassword;
    }

    // Publishing tab fields
    const publishMode = getIfExists("cfg-publish-mode");
    if (publishMode !== undefined) {
      patch.publishMode = publishMode === "static_html" ? "static_html" : undefined;
    }
    const blogPublishKey = getIfExists("cfg-blog-publish-key");
    if (blogPublishKey !== undefined) {
      patch.blogPublishKey = blogPublishKey || undefined;
    }
    const postsPerDay = getIfExists("cfg-posts-per-day");
    const draftsPerDay = getIfExists("cfg-drafts-per-day");
    const timeWindows = getIfExists("cfg-time-windows");
    const apwMin = getIfExists("cfg-apw-min");
    const apwMax = getIfExists("cfg-apw-max");
    if (
      postsPerDay !== undefined ||
      draftsPerDay !== undefined ||
      timeWindows !== undefined ||
      apwMin !== undefined ||
      apwMax !== undefined
    ) {
      // Both weekly fields filled → weekly cadence; otherwise keep daily mode
      // (and drop any stored weekly override so the form reflects the truth).
      const minN = Number.parseInt(apwMin ?? "", 10);
      const maxN = Number.parseInt(apwMax ?? "", 10);
      const articlesPerWeek =
        Number.isFinite(minN) && Number.isFinite(maxN) && minN > 0 && maxN >= minN
          ? { min: minN, max: maxN }
          : undefined;
      patch.publishSchedule = {
        postsPerDay: Number.parseInt(postsPerDay ?? "2", 10) || 2,
        draftsPerDay: Number.parseInt(draftsPerDay ?? "2", 10) || 2,
        timeWindows: (timeWindows ?? "")
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean),
        articlesPerWeek,
      };
    }
    const infoShare = getIfExists("cfg-info-share");
    const maxCards = getIfExists("cfg-max-cards");
    if (infoShare !== undefined || maxCards !== undefined) {
      const shareN = Number.parseFloat(infoShare ?? "");
      const cardsN = Number.parseInt(maxCards ?? "", 10);
      patch.contentMix = {
        informationalShare:
          Number.isFinite(shareN) && shareN > 0 ? Math.min(shareN, 100) / 100 : undefined,
        maxProductsPerArticle: Number.isFinite(cardsN) && cardsN > 0 ? cardsN : undefined,
      };
    }
    const monthlyBudgetVal = getIfExists("cfg-monthly-budget");
    if (monthlyBudgetVal !== undefined) {
      patch.monthlyBudgetUsd = monthlyBudgetVal ? Number.parseFloat(monthlyBudgetVal) || 0 : 0;
    }
    const hitlPhaseVal = getIfExists("cfg-hitl-phase");
    if (hitlPhaseVal !== undefined) {
      patch.hitlPhase = (Number.parseInt(hitlPhaseVal, 10) || 1) as 1 | 2 | 3;
    }
    const productsReqEl = document.getElementById(
      "cfg-products-required",
    ) as HTMLInputElement | null;
    if (productsReqEl) {
      patch.productsRequired = productsReqEl.checked;
    }
    const injectCtxEl = document.getElementById(
      "cfg-inject-contextual-products",
    ) as HTMLInputElement | null;
    if (injectCtxEl) {
      patch.injectContextualProducts = injectCtxEl.checked;
    }

    // Amazon tab fields
    const amzTag = getIfExists("cfg-amz-tag");
    const amzDomain = getIfExists("cfg-amz-domain");
    const amzMinPrice = getIfExists("cfg-amz-min-price");
    const amzMinRating = getIfExists("cfg-amz-min-rating");
    const amzMinReviews = getIfExists("cfg-amz-min-reviews");
    const amzMinerEl = document.getElementById("cfg-amz-miner") as HTMLInputElement | null;
    const amzSourcerEl = document.getElementById("cfg-amz-sourcer") as HTMLInputElement | null;

    // Only include amazonSource if the Amazon tab has been rendered (tag field exists)
    if (amzTag !== undefined) {
      const existingAmz = _state.site?.amazonSource;
      patch.amazonSource = {
        tag: amzTag || existingAmz?.tag || "",
        domain: amzDomain || existingAmz?.domain || "amazon.com",
        categories: existingAmz?.categories || [],
        minPrice: amzMinPrice ? Number.parseFloat(amzMinPrice) : (existingAmz?.minPrice ?? 30),
        minRating: amzMinRating ? Number.parseFloat(amzMinRating) : (existingAmz?.minRating ?? 4.0),
        minReviews: amzMinReviews
          ? Number.parseInt(amzMinReviews, 10)
          : (existingAmz?.minReviews ?? 100),
        minerEnabled: amzMinerEl ? amzMinerEl.checked : (existingAmz?.minerEnabled ?? true),
        sourcerEnabled: amzSourcerEl ? amzSourcerEl.checked : (existingAmz?.sourcerEnabled ?? true),
        usedAsins: existingAmz?.usedAsins || [],
      };
    }

    // Pinterest tab fields — only included if the tab was rendered (enable toggle exists)
    const pinEnabledEl = document.getElementById("cfg-pin-enabled") as HTMLInputElement | null;
    if (pinEnabledEl) {
      const existingPin = _state.site?.pinterest;
      const pinInt = (id: string, def: number): number => {
        const v = getIfExists(id);
        return v ? Number.parseInt(v, 10) || def : def;
      };
      // Blank secret/token fields fall back to the stored value (so you don't re-enter tokens every save)
      const keep = (id: string, prev: string | undefined): string | undefined => {
        const v = getIfExists(id);
        return v !== undefined && v !== "" ? v : prev;
      };
      patch.pinterest = {
        enabled: pinEnabledEl.checked,
        appId: keep("cfg-pin-app-id", existingPin?.appId),
        appSecret: keep("cfg-pin-app-secret", existingPin?.appSecret),
        accessToken: keep("cfg-pin-access-token", existingPin?.accessToken),
        refreshToken: keep("cfg-pin-refresh-token", existingPin?.refreshToken),
        tokenExpiresAt: existingPin?.tokenExpiresAt,
        dailyPinCap: pinInt("cfg-pin-daily-cap", existingPin?.dailyPinCap ?? 8),
        activeHours: [
          pinInt("cfg-pin-active-start", existingPin?.activeHours?.[0] ?? 9),
          pinInt("cfg-pin-active-end", existingPin?.activeHours?.[1] ?? 21),
        ],
        variantsPerArticle: pinInt("cfg-pin-variants", existingPin?.variantsPerArticle ?? 3),
        boardCreatesPerDay: pinInt("cfg-pin-board-creates", existingPin?.boardCreatesPerDay ?? 2),
        articleRepinGapDays: pinInt("cfg-pin-article-gap", existingPin?.articleRepinGapDays ?? 3),
        brandLabel: keep("cfg-pin-brand-label", existingPin?.brandLabel),
        templateWeights: (() => {
          // parseInt-or-default that PRESERVES an explicit 0 (0 disables a template)
          const w0 = (id: string, def: number): number => {
            const v = getIfExists(id);
            if (v === undefined || v === "") {
              return def;
            }
            const n = Number.parseInt(v, 10);
            return Number.isNaN(n) || n < 0 ? def : n;
          };
          const tw = existingPin?.templateWeights;
          return {
            "hero-card": w0("cfg-pin-w-hero-card", tw?.["hero-card"] ?? 3),
            "hero-clean": w0("cfg-pin-w-hero-clean", tw?.["hero-clean"] ?? 2),
            collage: w0("cfg-pin-w-collage", tw?.collage ?? 2),
            infographic: w0("cfg-pin-w-infographic", tw?.infographic ?? 1),
            minimal: w0("cfg-pin-w-minimal", tw?.minimal ?? 1),
            photo: w0("cfg-pin-w-photo", tw?.photo ?? 2),
          };
        })(),
      };
    }

    // Agent Prompts tab fields
    const strategistP = getTaIfExists("cfg-prompt-strategist");
    if (strategistP !== undefined) {
      patch.strategistPrompt = strategistP || undefined;
    }
    const writerP = getTaIfExists("cfg-prompt-writer");
    if (writerP !== undefined) {
      patch.writerPrompt = writerP || undefined;
    }
    const linkerP = getTaIfExists("cfg-prompt-linker");
    if (linkerP !== undefined) {
      patch.linkerPrompt = linkerP || undefined;
    }
    const artistP = getTaIfExists("cfg-prompt-artist");
    if (artistP !== undefined) {
      patch.artistPrompt = artistP || undefined;
    }
    const arthurP = getTaIfExists("cfg-prompt-arthur");
    if (arthurP !== undefined) {
      patch.arthurPrompt = arthurP || undefined;
    }
    const minerP = getTaIfExists("cfg-prompt-miner");
    if (minerP !== undefined) {
      patch.minerPrompt = minerP || undefined;
    }

    if (_createMode && !_state.site?.id) {
      // Create new site
      if (!patch.name || !patch.url) {
        throw new Error("Site name and URL are required");
      }
      const res = await _client.request<SiteData & { site?: SiteData }>(
        "contentforge.sites.create",
        patch,
      );
      // Handler responds with site directly OR { site: ... }
      const newSite = res?.site ?? (res?.id ? res : null);
      if (newSite?.id) {
        _state.site = newSite;
        _createMode = false;
        _onProjectCreated?.(newSite.id);
      }
    } else {
      const existingSiteId = _state.site?.id;
      if (!existingSiteId) {
        throw new Error("No site to update");
      }
      await _client.request("contentforge.sites.update", { id: existingSiteId, patch });

      // Also save CSV affiliate params if the Product Sources tab is rendered
      for (const csv of _state.csvSources) {
        const affEl = document.getElementById(`csv-aff-${csv.id}`) as HTMLInputElement | null;
        const midEl = document.getElementById(`csv-mid-${csv.id}`) as HTMLInputElement | null;
        const stEl = document.getElementById(`csv-st-${csv.id}`) as HTMLInputElement | null;
        // Only save if at least one input exists in the DOM (tab is rendered)
        if (affEl || midEl || stEl) {
          const affCode = affEl?.value || undefined;
          const merchantId = midEl?.value || undefined;
          const trackingRef = stEl?.value || undefined;
          // Only send update if any value is set (skip empty sources)
          if (affCode || merchantId || trackingRef) {
            await _client.request("contentforge.csv.updateParams", {
              csvId: csv.id,
              affCode,
              merchantId,
              trackingRef,
            });
            csv.affCode = affCode;
            csv.merchantId = merchantId;
            csv.trackingRef = trackingRef;
          }
        }
      }

      // Reload site data
      const site = await _client.request<SiteData>("contentforge.sites.get", {
        id: existingSiteId,
      });
      _state.site = site;
    }
    _state.saving = false;
    _state.saved = true;

    // Clear saved indicator after 3s
    setTimeout(() => {
      _state.saved = false;
      _onRefresh?.();
    }, 3000);
  } catch (err) {
    _state.error = (err as Error).message;
    _state.saving = false;
  }
  _onRefresh?.();
}

async function testWpConnection() {
  if (!_client) {
    return;
  }

  // Read from form input first (handles both new and existing projects)
  const form = document.getElementById("cf-settings-form") as HTMLFormElement | null;
  const wpApiUrl =
    (form?.querySelector("#cfg-wp-api") as HTMLInputElement)?.value || _state.site?.wpApiUrl || "";

  if (!wpApiUrl) {
    _state.wpTestResult = "❌ Enter a WP REST API URL first";
    _onRefresh?.();
    return;
  }

  _state = { ..._state, wpTesting: true, wpTestResult: null };
  _onRefresh?.();

  try {
    // Route through gateway to avoid CORS
    const res = await _client.request<{ success: boolean; siteName?: string; error?: string }>(
      "contentforge.wp.test",
      { wpApiUrl },
    );

    if (res?.success) {
      _state.wpTestResult = `✅ Connected! Site: "${res.siteName || "WordPress"}"`;
    } else {
      _state.wpTestResult = `❌ Error: ${res?.error || "Unknown error"}`;
    }
  } catch (err) {
    _state.wpTestResult = `❌ Connection failed: ${String(err)}`;
  }

  _state.wpTesting = false;
  _onRefresh?.();
}

function resetPrompt(agentKey: string) {
  if (!_state.defaults) {
    return;
  }
  const textarea = document.getElementById(`cfg-prompt-${agentKey}`) as HTMLTextAreaElement | null;
  if (textarea) {
    const key = `${agentKey}Prompt` as keyof DefaultPrompts;
    textarea.value = _state.defaults[key] as string;
  }
}

// ─── Render ─────────────────────────────────────────────────────────────

export function renderSettingsView(
  dashboard: ContentForgeDashboardData | null,
  client: GatewayBrowserClient | null,
  onRefresh: () => void,
  activeSiteId?: string | null,
  createMode?: boolean,
  onProjectCreated?: (siteId: string) => void,
): TemplateResult {
  _client = client;
  _onRefresh = onRefresh;
  _onProjectCreated = onProjectCreated ?? null;

  // Detect if activeSiteId or createMode changed — re-init
  const needsReload =
    !_initialized ||
    _createMode !== (createMode ?? false) ||
    _loadForSiteId !== (activeSiteId ?? null);

  if (needsReload && client) {
    _initialized = true;
    _createMode = createMode ?? false;
    _loadForSiteId = activeSiteId ?? null;
    // Reset source loaded flags so they reload for the new project
    _state.csvSourcesLoaded = false;
    _state.webSourcesLoaded = false;
    _state.csvSources = [];
    _state.webSources = [];
    void loadSettingsData(client, dashboard, activeSiteId);
  }

  if (_state.loading) {
    return html`
      <div class="cf-settings">
        <div class="cf-loading"><span class="cf-spinner"></span> Loading settings...</div>
      </div>
    `;
  }

  if (!_state.site) {
    return html`
      <div class="cf-settings">
        <div class="cf-settings__empty">
          <h2>No project configured</h2>
          <p>Create a project first in the Content view to configure settings.</p>
          ${_state.error ? html`<p style="color: #f87171; margin-top: 12px;">Error: ${_state.error}</p>` : ""}
        </div>
      </div>
    `;
  }

  const site = _state.site;
  const defaults = _state.defaults;

  const sections = [
    { key: "site", label: "🌐 Site Info" },
    { key: "wordpress", label: "📡 WordPress" },
    { key: "publishing", label: "📅 Publishing" },
    { key: "prompts", label: "🤖 Agent Prompts" },
    { key: "sources", label: "📦 Product Sources" },
    { key: "amazon", label: "🛒 Amazon" },
    { key: "pinterest", label: "📌 Pinterest" },
  ];

  return html`
    <div class="cf-settings">
      <div class="cf-settings__header">
        <h1 class="cf-settings__title">${_createMode ? "➕ New Project" : "⚙️ Project Settings"}</h1>
        <div class="cf-settings__site-name">${_createMode ? "Fill in the details below" : site.name || site.url}</div>
      </div>

      ${_state.error ? html`<div class="cf-settings__error">❌ ${_state.error}</div>` : nothing}
      ${
        _state.saved
          ? html`
              <div class="cf-settings__success">✅ ${_createMode ? "Project created!" : "Settings saved successfully"}</div>
            `
          : nothing
      }

      <div class="cf-settings__tabs">
        ${sections.map(
          (s) => html`
            <button
              class="cf-settings__tab ${_state.activeSection === s.key ? "cf-settings__tab--active" : ""}"
              @click=${() => {
                _state.activeSection = s.key;
                _onRefresh?.();
              }}
            >
              ${s.label}
            </button>
          `,
        )}
      </div>

      <form id="cf-settings-form" class="cf-settings__form" @submit=${(e: Event) => e.preventDefault()}>
        ${_state.activeSection === "site" ? renderSiteSection(site) : nothing}
        ${_state.activeSection === "wordpress" ? renderWordPressSection(site) : nothing}
        ${_state.activeSection === "publishing" ? renderPublishingSection(site) : nothing}
        ${_state.activeSection === "prompts" ? renderPromptsSection(site, defaults) : nothing}
        ${_state.activeSection === "sources" ? renderProductSourcesSection() : nothing}
        ${_state.activeSection === "amazon" ? renderAmazonSection(site) : nothing}
        ${_state.activeSection === "pinterest" ? renderPinterestSection(site) : nothing}

        <div class="cf-settings__actions">
          <button
            class="cf-settings__btn cf-settings__btn--primary"
            @click=${saveSiteSettings}
            ?disabled=${_state.saving}
          >
            ${_state.saving ? "Saving..." : _createMode ? "✨ Create Project" : "💾 Save Settings"}
          </button>
        </div>
      </form>
    </div>
  `;
}

// ─── Section Renderers ──────────────────────────────────────────────────

function renderSiteSection(site: SiteData) {
  return html`
    <div class="cf-settings__section">
      <h2 class="cf-settings__section-title">Site Information</h2>
      <div class="cf-settings__field">
        <label for="cfg-name">Site Name</label>
        <input id="cfg-name" type="text" .value=${site.name} placeholder="My Website" />
      </div>
      <div class="cf-settings__field">
        <label for="cfg-url">Site URL</label>
        <input id="cfg-url" type="url" .value=${site.url} placeholder="https://example.com" />
      </div>
      <div class="cf-settings__field">
        <label for="cfg-sitemap">Primary Sitemap URL</label>
        <input
          id="cfg-sitemap"
          type="url"
          .value=${site.sitemapUrl}
          placeholder="https://example.com/post-sitemap.xml"
        />
        <div id="cfg-sitemaps-extra" style="margin-top: 8px;">
          ${(site.sitemapUrls ?? []).map(
            (u: string, i: number) => html`
              <div style="display: flex; gap: 6px; margin-top: 6px; align-items: center;" data-sitemap-extra="${i}">
                <input
                  type="url"
                  class="cfg-sitemap-extra-input"
                  .value=${u}
                  placeholder="https://example.com/page-sitemap.xml"
                  style="flex: 1;"
                />
                <button
                  type="button"
                  style="padding: 4px 8px; background: var(--cf-bg-tertiary); border: 1px solid var(--cf-border); border-radius: 6px; cursor: pointer; color: var(--cf-text-secondary); font-size: 13px;"
                  @click=${(e: Event) => {
                    const row = (e.target as HTMLElement).closest("[data-sitemap-extra]");
                    row?.remove();
                  }}
                  title="Remove sitemap"
                >&times;</button>
              </div>
            `,
          )}
        </div>
        <button
          type="button"
          style="margin-top: 8px; padding: 4px 12px; background: var(--cf-bg-tertiary); border: 1px solid var(--cf-border); border-radius: 6px; cursor: pointer; color: var(--cf-text-secondary); font-size: 13px; display: flex; align-items: center; gap: 4px;"
          @click=${() => {
            const container = document.getElementById("cfg-sitemaps-extra");
            if (!container) {
              return;
            }
            const idx = container.children.length;
            const row = document.createElement("div");
            row.style.cssText = "display: flex; gap: 6px; margin-top: 6px; align-items: center;";
            row.setAttribute("data-sitemap-extra", String(idx));
            row.innerHTML = `<input type="url" class="cfg-sitemap-extra-input" placeholder="https://example.com/page-sitemap.xml" style="flex: 1;" /><button type="button" style="padding: 4px 8px; background: var(--cf-bg-tertiary); border: 1px solid var(--cf-border); border-radius: 6px; cursor: pointer; color: var(--cf-text-secondary); font-size: 13px;" title="Remove sitemap">&times;</button>`;
            row.querySelector("button")?.addEventListener("click", () => row.remove());
            container.appendChild(row);
          }}
        >+ Add Sitemap</button>
      </div>

      <h3 style="margin-top: 24px; font-size: 14px; color: var(--cf-text-secondary);">🔍 Google & Bing Integration</h3>
      <div class="cf-source-add__grid">
        <div class="cf-settings__field">
          <label for="cfg-gsc-url">GSC Site URL</label>
          <input id="cfg-gsc-url" type="text" .value=${site.gscSiteUrl ?? ""} placeholder="sc-domain:durerestop.ro" />
          <label style="display: flex; align-items: center; gap: 8px; margin-top: 6px; font-size: 13px; cursor: pointer;">
            <input id="cfg-gsc-miner" type="checkbox" .checked=${site.gscMinerEnabled !== false} />
            Use GSC in Miner
          </label>
        </div>
        <div class="cf-settings__field">
          <label for="cfg-bing-url">Bing Site URL</label>
          <input id="cfg-bing-url" type="text" .value=${site.bingSiteUrl ?? ""} placeholder="https://durerestop.ro" />
          <label style="display: flex; align-items: center; gap: 8px; margin-top: 6px; font-size: 13px; cursor: pointer;">
            <input id="cfg-bing-miner" type="checkbox" .checked=${site.bingMinerEnabled !== false} />
            Use Bing in Miner
          </label>
        </div>
        <div class="cf-settings__field">
          <label for="cfg-geo-target">Geo Target</label>
          <input id="cfg-geo-target" type="number" .value=${String(site.geoTarget ?? "")} placeholder="2642 (Romania) / 2840 (US)" />
        </div>
        <div class="cf-settings__field">
          <label for="cfg-lang-code">Language Code</label>
          <input id="cfg-lang-code" type="number" .value=${String(site.languageCode ?? "")} placeholder="1027 (RO) / 1000 (EN)" />
        </div>
        <div class="cf-settings__field">
          <label for="cfg-site-lang">Site Language</label>
          <input id="cfg-site-lang" type="text" .value=${site.siteLang ?? ""} placeholder="ro or en" />
        </div>
      </div>
    </div>
  `;
}

function renderWordPressSection(site: SiteData) {
  return html`
    <div class="cf-settings__section">
      <h2 class="cf-settings__section-title">WordPress Connection</h2>
      <div class="cf-settings__field">
        <label for="cfg-wp-api">WP REST API URL</label>
        <input
          id="cfg-wp-api"
          type="url"
          .value=${site.wpApiUrl}
          placeholder="https://example.com/wp-json/wp/v2"
        />
        <span class="cf-settings__hint">Usually: your-site.com/wp-json/wp/v2</span>
      </div>
      <div class="cf-settings__field">
        <label for="cfg-wp-user">WP Username</label>
        <input id="cfg-wp-user" type="text" .value=${site.wpUsername} placeholder="admin" />
      </div>
      <div class="cf-settings__field">
        <label for="cfg-wp-pass">WP Application Password</label>
        <input
          id="cfg-wp-pass"
          type="password"
          .value=${site.wpAppPassword}
          placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
        />
        <span class="cf-settings__hint">Generate at: WP Admin → Users → Application Passwords</span>
      </div>
      <div class="cf-settings__field">
        <button
          type="button"
          class="cf-settings__btn cf-settings__btn--secondary"
          @click=${testWpConnection}
          ?disabled=${_state.wpTesting}
        >
          ${_state.wpTesting ? "Testing..." : "🔌 Test Connection"}
        </button>
        ${
          _state.wpTestResult
            ? html`<span class="cf-settings__test-result">${_state.wpTestResult}</span>`
            : nothing
        }
      </div>
    </div>
  `;
}

function renderPinterestSection(site: SiteData) {
  const pin = site.pinterest ?? {};
  const [activeStart, activeEnd] = pin.activeHours ?? [9, 21];
  return html`
    <div class="cf-settings__section">
      <h2 class="cf-settings__section-title">📌 Pinterest Automation</h2>

      <div class="cf-settings__field">
        <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
          <input type="checkbox" id="cfg-pin-enabled" ?checked=${pin.enabled ?? false} />
          Enable Pinterest automation
        </label>
        <span class="cf-settings__hint">
          When on, every published article generates fresh pins (PinDesigner) that drip out to
          matched boards (PinPublisher). Off = no pins are created or posted.
        </span>
      </div>

      <h2 class="cf-settings__section-title" style="margin-top: 24px;">OAuth Credentials</h2>
      <span class="cf-settings__hint" style="display:block; margin-bottom: 12px;">
        From your app at developers.pinterest.com (scopes: boards:read/write, pins:read/write).
        ⚠️ Trial-tier pins are invisible to the public — submit the video demo for Standard access before expecting traffic.
      </span>
      <div class="cf-source-add__grid">
        <div class="cf-settings__field">
          <label for="cfg-pin-app-id">App ID</label>
          <input id="cfg-pin-app-id" type="text" .value=${pin.appId ?? ""} placeholder="Pinterest app ID" />
        </div>
        <div class="cf-settings__field">
          <label for="cfg-pin-app-secret">App Secret</label>
          <input id="cfg-pin-app-secret" type="password" .value=${pin.appSecret ?? ""} placeholder="Pinterest app secret" />
        </div>
      </div>
      <div class="cf-source-add__grid">
        <div class="cf-settings__field">
          <label for="cfg-pin-access-token">Access Token</label>
          <input id="cfg-pin-access-token" type="password" .value=${pin.accessToken ?? ""} placeholder="OAuth access token" />
        </div>
        <div class="cf-settings__field">
          <label for="cfg-pin-refresh-token">Refresh Token</label>
          <input id="cfg-pin-refresh-token" type="password" .value=${pin.refreshToken ?? ""} placeholder="OAuth refresh token (auto-refreshes)" />
        </div>
      </div>
      <span class="cf-settings__hint">Leave a secret/token blank to keep the stored value.</span>

      <h2 class="cf-settings__section-title" style="margin-top: 24px;">Posting Behavior</h2>
      <div class="cf-settings__field">
        <label for="cfg-pin-brand-label">Brand Label</label>
        <input
          id="cfg-pin-brand-label"
          type="text"
          .value=${pin.brandLabel ?? ""}
          placeholder="RAISEDINDEPENDENT.COM"
        />
        <span class="cf-settings__hint">Text rendered on every pin. Defaults to the site domain if blank.</span>
      </div>
      <div class="cf-source-add__grid">
        <div class="cf-settings__field">
          <label for="cfg-pin-daily-cap">Daily Pin Cap</label>
          <input id="cfg-pin-daily-cap" type="number" min="1" max="25" .value=${String(pin.dailyPinCap ?? 8)} />
          <span class="cf-settings__hint">Max pins/day across the account. Start low (3–5), raise to 10–12 by month 3.</span>
        </div>
        <div class="cf-settings__field">
          <label for="cfg-pin-variants">Pins per Article</label>
          <input id="cfg-pin-variants" type="number" min="1" max="5" .value=${String(pin.variantsPerArticle ?? 3)} />
          <span class="cf-settings__hint">Fresh pin variants generated per article (one per matched board).</span>
        </div>
      </div>
      <div class="cf-source-add__grid">
        <div class="cf-settings__field">
          <label for="cfg-pin-active-start">Active Hours — Start</label>
          <input id="cfg-pin-active-start" type="number" min="0" max="23" .value=${String(activeStart)} />
        </div>
        <div class="cf-settings__field">
          <label for="cfg-pin-active-end">Active Hours — End</label>
          <input id="cfg-pin-active-end" type="number" min="1" max="24" .value=${String(activeEnd)} />
        </div>
      </div>
      <span class="cf-settings__hint">Pins only post inside this local window (uses the site timezone offset).</span>
      <div class="cf-source-add__grid">
        <div class="cf-settings__field">
          <label for="cfg-pin-board-creates">Board Creates per Day</label>
          <input id="cfg-pin-board-creates" type="number" min="0" max="10" .value=${String(pin.boardCreatesPerDay ?? 2)} />
          <span class="cf-settings__hint">
            Anti-spam cap. Boards are matched to your existing boards by name; missing ones are created gradually.
          </span>
        </div>
        <div class="cf-settings__field">
          <label for="cfg-pin-article-gap">Article Re-pin Gap (days)</label>
          <input id="cfg-pin-article-gap" type="number" min="1" max="14" .value=${String(pin.articleRepinGapDays ?? 3)} />
          <span class="cf-settings__hint">
            Min days between pins to the SAME article URL. Young accounts: 3; raise toward 7 as the account matures.
          </span>
        </div>
      </div>

      <h2 class="cf-settings__section-title" style="margin-top: 24px;">Template Mix</h2>
      <span class="cf-settings__hint" style="display:block; margin-bottom: 12px;">
        Relative weight per template — how often each is used (0 disables it). Text-overlay templates
        drive clicks; the clean Photo template drives saves/reach. The learning loop will tune these
        automatically once pin analytics exist.
      </span>
      <div class="cf-source-add__grid">
        <div class="cf-settings__field">
          <label for="cfg-pin-w-hero-card">Hero Card (text)</label>
          <input id="cfg-pin-w-hero-card" type="number" min="0" max="10" .value=${String(pin.templateWeights?.["hero-card"] ?? 3)} />
        </div>
        <div class="cf-settings__field">
          <label for="cfg-pin-w-hero-clean">Hero Clean (text)</label>
          <input id="cfg-pin-w-hero-clean" type="number" min="0" max="10" .value=${String(pin.templateWeights?.["hero-clean"] ?? 2)} />
        </div>
      </div>
      <div class="cf-source-add__grid">
        <div class="cf-settings__field">
          <label for="cfg-pin-w-collage">Collage (text)</label>
          <input id="cfg-pin-w-collage" type="number" min="0" max="10" .value=${String(pin.templateWeights?.collage ?? 2)} />
        </div>
        <div class="cf-settings__field">
          <label for="cfg-pin-w-infographic">Infographic (text)</label>
          <input id="cfg-pin-w-infographic" type="number" min="0" max="10" .value=${String(pin.templateWeights?.infographic ?? 1)} />
        </div>
      </div>
      <div class="cf-source-add__grid">
        <div class="cf-settings__field">
          <label for="cfg-pin-w-minimal">Minimal (text)</label>
          <input id="cfg-pin-w-minimal" type="number" min="0" max="10" .value=${String(pin.templateWeights?.minimal ?? 1)} />
        </div>
        <div class="cf-settings__field">
          <label for="cfg-pin-w-photo">Photo (clean, no text)</label>
          <input id="cfg-pin-w-photo" type="number" min="0" max="10" .value=${String(pin.templateWeights?.photo ?? 2)} />
        </div>
      </div>
    </div>
  `;
}

function renderPublishingSection(site: SiteData) {
  const schedule = site.publishSchedule ?? {
    postsPerDay: 2,
    draftsPerDay: 2,
    timeWindows: ["09:00-12:00", "14:00-18:00"],
  };
  return html`
    <div class="cf-settings__section">
      <h2 class="cf-settings__section-title">Publish Mode</h2>
      <div class="cf-source-add__grid">
        <div class="cf-settings__field">
          <label for="cfg-publish-mode">Publish Mode</label>
          <select id="cfg-publish-mode" .value=${site.publishMode ?? "wordpress"}>
            <option value="wordpress" ?selected=${(site.publishMode ?? "wordpress") === "wordpress"}>WordPress (REST API)</option>
            <option value="static_html" ?selected=${site.publishMode === "static_html"}>Static HTML (PHP API)</option>
          </select>
          <span class="cf-settings__hint">
            ${
              site.publishMode === "static_html"
                ? "Articles are POSTed as HTML to a custom PHP endpoint. No WordPress needed."
                : "Articles are published via WordPress REST API using credentials above."
            }
          </span>
        </div>
        <div class="cf-settings__field">
          <label for="cfg-blog-publish-key">Blog Publish Key</label>
          <input
            id="cfg-blog-publish-key"
            type="password"
            .value=${site.blogPublishKey ?? ""}
            placeholder="Bearer token for static HTML publisher"
          />
          <span class="cf-settings__hint">Required for Static HTML mode. This key authenticates POST requests to the blog API.</span>
        </div>
      </div>

      <h2 class="cf-settings__section-title" style="margin-top: 24px;">Publishing Schedule</h2>
      <div class="cf-source-add__grid">
        <div class="cf-settings__field">
          <label for="cfg-posts-per-day">Posts per Day</label>
          <input
            id="cfg-posts-per-day"
            type="number"
            min="1"
            max="10"
            .value=${String(schedule.postsPerDay)}
          />
          <span class="cf-settings__hint">How many articles to publish per day</span>
        </div>
        <div class="cf-settings__field">
          <label for="cfg-drafts-per-day">Drafts per Day</label>
          <input
            id="cfg-drafts-per-day"
            type="number"
            min="1"
            max="10"
            .value=${String(schedule.draftsPerDay ?? 2)}
          />
          <span class="cf-settings__hint">Max articles the pipeline writes per day. Controls how fast outlines, sourcing, and writing runs. Ignored while Articles per Week is set.</span>
        </div>
        <div class="cf-settings__field">
          <label for="cfg-apw-min">Articles per Week — min</label>
          <input
            id="cfg-apw-min"
            type="number"
            min="0"
            max="7"
            .value=${schedule.articlesPerWeek ? String(schedule.articlesPerWeek.min) : ""}
            placeholder="empty = daily mode"
          />
          <span class="cf-settings__hint">Weekly cadence on randomized days (overrides Drafts per Day; max 1 article/day). Each week gets a quota between min and max, written on randomly chosen days.</span>
        </div>
        <div class="cf-settings__field">
          <label for="cfg-apw-max">Articles per Week — max</label>
          <input
            id="cfg-apw-max"
            type="number"
            min="0"
            max="7"
            .value=${schedule.articlesPerWeek ? String(schedule.articlesPerWeek.max) : ""}
            placeholder="empty = daily mode"
          />
          <span class="cf-settings__hint">Fill BOTH fields to enable weekly cadence. Clear either one to go back to Drafts per Day.</span>
        </div>
      </div>

      <h2 class="cf-settings__section-title" style="margin-top: 24px;">Content Mix</h2>
      <div class="cf-source-add__grid">
        <div class="cf-settings__field">
          <label for="cfg-info-share">Informational share (%)</label>
          <input
            id="cfg-info-share"
            type="number"
            min="0"
            max="100"
            .value=${site.contentMix?.informationalShare ? String(Math.round(site.contentMix.informationalShare * 100)) : ""}
            placeholder="0 = off"
          />
          <span class="cf-settings__hint">Target share of informational (product-free) articles over the last 14 days. The Miner keeps a stock of AI-generated informational keywords and the pipeline prefers them while below target.</span>
        </div>
        <div class="cf-settings__field">
          <label for="cfg-max-cards">Max product cards per article</label>
          <input
            id="cfg-max-cards"
            type="number"
            min="1"
            max="10"
            .value=${site.contentMix?.maxProductsPerArticle ? String(site.contentMix.maxProductsPerArticle) : ""}
            placeholder="empty = no cap"
          />
          <span class="cf-settings__hint">Hard cap applied in the Sourcer after ranking. Keeps affiliate density down.</span>
        </div>
      </div>
      <div class="cf-settings__field">
        <label for="cfg-time-windows">Time Windows</label>
        <input
          id="cfg-time-windows"
          type="text"
          .value=${schedule.timeWindows.join(", ")}
          placeholder="09:00-12:00, 14:00-18:00"
        />
        <span class="cf-settings__hint">Comma-separated time ranges (24h format). Posts are spaced at least 3 hours apart.</span>
      </div>
      <div class="cf-settings__field">
        <label for="cfg-monthly-budget">Monthly Budget (USD)</label>
        <input
          id="cfg-monthly-budget"
          type="number"
          min="0"
          step="1"
          .value=${String(site.monthlyBudgetUsd ?? 0)}
          placeholder="0 = no limit"
        />
        <span class="cf-settings__hint">Pipeline stops when monthly spending exceeds this amount. 0 = unlimited.</span>
      </div>

      <h2 class="cf-settings__section-title" style="margin-top: 24px;">🎩 Arthur Trust Level</h2>
      <div class="cf-settings__field">
        <label for="cfg-hitl-phase">Autonomy Phase</label>
        <select id="cfg-hitl-phase" .value=${String(site.hitlPhase ?? 1)}>
          <option value="1" ?selected=${(site.hitlPhase ?? 1) === 1}>Phase 1 — Trust Building (Boss approves everything)</option>
          <option value="2" ?selected=${(site.hitlPhase ?? 1) === 2}>Phase 2 — Semi-Auto (Boss approves drafts only)</option>
          <option value="3" ?selected=${(site.hitlPhase ?? 1) === 3}>Phase 3 — Full Auto (Arthur handles everything)</option>
        </select>
        <span class="cf-settings__hint">
          ${(site.hitlPhase ?? 1) === 1 ? "Arthur sends outlines, products, and drafts for your approval before publishing." : (site.hitlPhase ?? 1) === 2 ? "Arthur auto-approves outlines & products. You only review drafts before publishing." : "Arthur runs fully autonomously. You still get Telegram previews + cost for every article."}
        </span>
      </div>

      <div class="cf-settings__field">
        <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
          <input type="checkbox" id="cfg-products-required" ?checked=${site.productsRequired !== false} />
          Products Required
        </label>
        <span class="cf-settings__hint">
          ${
            site.productsRequired !== false
              ? "Articles require product cards (Sourcer runs, Writer needs products)."
              : "Articles can be written without products — informational with CTA demo links. Sourcer is skipped."
          }
        </span>
      </div>

      <div class="cf-settings__field">
        <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
          <input type="checkbox" id="cfg-inject-contextual-products" ?checked=${site.injectContextualProducts ?? false} />
          Inject Contextual Products
        </label>
        <span class="cf-settings__hint">
          After Writer generates an article, scan text for product/solution mentions and insert 1-product cards after relevant paragraphs.
        </span>
      </div>
    </div>
  `;
}

function renderPromptsSection(site: SiteData, defaults: DefaultPrompts | null) {
  const agents = [
    { key: "strategist", label: "📋 Strategist", desc: "SEO outline generation rules" },
    { key: "writer", label: "✍️ Writer", desc: "Article tone, style, and formatting" },
    { key: "linker", label: "🔗 Linker", desc: "Internal link integration rules" },
    { key: "artist", label: "🎨 Artist", desc: "Featured image generation style" },
    { key: "arthur", label: "🎩 Arthur", desc: "Manager personality and quality rules" },
    { key: "miner", label: "⛏️ Miner", desc: "Keyword discovery strategy" },
  ];

  return html`
    <div class="cf-settings__section">
      <h2 class="cf-settings__section-title">Agent Prompts</h2>
      <p class="cf-settings__section-desc">
        Customize how each agent behaves for this project. Leave empty to use defaults.
      </p>
      ${agents.map((agent) => {
        const promptKey = `${agent.key}Prompt` as keyof SiteData;
        const defaultKey = `${agent.key}Prompt` as keyof DefaultPrompts;
        const currentValue =
          (site[promptKey] as string) || (defaults ? (defaults[defaultKey] as string) : "");

        return html`
          <div class="cf-settings__prompt-block">
            <div class="cf-settings__prompt-header">
              <label for="cfg-prompt-${agent.key}">${agent.label}</label>
              <span class="cf-settings__prompt-desc">${agent.desc}</span>
              <button
                type="button"
                class="cf-settings__btn cf-settings__btn--ghost"
                @click=${() => resetPrompt(agent.key)}
                title="Reset to default prompt"
              >
                ↻ Reset
              </button>
            </div>
            <textarea
              id="cfg-prompt-${agent.key}"
              class="cf-settings__textarea"
              rows="8"
              placeholder="Enter custom prompt..."
              .value=${currentValue}
            ></textarea>
          </div>
        `;
      })}
    </div>
  `;
}

// ─── Product Sources Section ────────────────────────────────────────────

async function loadCsvSources() {
  if (!_client || _state.csvSourcesLoaded) {
    return;
  }
  try {
    const siteId = _state.site?.id;
    const res = await _client.request<{
      csvSources: Array<{
        id: string;
        filename: string;
        productCount: number;
        active: boolean;
        mineKeywords?: boolean;
        affCode?: string;
        merchantId?: string;
        trackingRef?: string;
      }>;
    }>("contentforge.csv.list", { siteId: siteId ?? undefined });
    _state.csvSources = res?.csvSources ?? [];
    _state.csvSourcesLoaded = true;
  } catch (err) {
    console.warn("[CF] Failed to load CSV sources:", err);
  }
  _onRefresh?.();
}

/** Save 2Performant affiliate params for a CSV source */
async function saveCsvAffiliateParams(csvId: string) {
  if (!_client) {
    return;
  }
  const affCode = (document.getElementById(`csv-aff-${csvId}`) as HTMLInputElement)?.value ?? "";
  const merchantId = (document.getElementById(`csv-mid-${csvId}`) as HTMLInputElement)?.value ?? "";
  const trackingRef = (document.getElementById(`csv-st-${csvId}`) as HTMLInputElement)?.value ?? "";
  const btn = document.querySelector(`[data-save-csv="${csvId}"]`);
  try {
    await _client.request("contentforge.csv.updateParams", {
      csvId,
      affCode: affCode || undefined,
      merchantId: merchantId || undefined,
      trackingRef: trackingRef || undefined,
    });
    // Update local state
    const csv = _state.csvSources.find((c) => c.id === csvId);
    if (csv) {
      csv.affCode = affCode || undefined;
      csv.merchantId = merchantId || undefined;
      csv.trackingRef = trackingRef || undefined;
    }
    // Visual feedback
    if (btn) {
      btn.textContent = "✅";
      setTimeout(() => {
        btn.textContent = "💾";
      }, 1500);
    }
  } catch (err) {
    console.warn("[CF] Failed to save affiliate params:", err);
    if (btn) {
      btn.textContent = "❌";
      setTimeout(() => {
        btn.textContent = "💾";
      }, 2000);
    }
  }
}

async function uploadCsvAction() {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".csv,.xml";
  input.addEventListener("change", async () => {
    const file = input.files?.[0];
    if (!file || !_client) {
      return;
    }
    const reader = new FileReader();
    reader.addEventListener("load", async () => {
      try {
        const siteId = _state.site?.id;
        await _client!.request("contentforge.csv.upload", {
          siteId: siteId ?? "default",
          filename: file.name,
          content: reader.result as string,
        });
        _state.csvSourcesLoaded = false;
        void loadCsvSources();
      } catch (err) {
        console.error("[CF] CSV upload failed:", err);
      }
    });
    reader.readAsText(file);
  });
  input.click();
}

async function toggleCsvAction(id: string, active: boolean) {
  if (!_client) {
    return;
  }
  try {
    await _client.request("contentforge.csv.toggle", { id, active });
    const csv = _state.csvSources.find((c) => c.id === id);
    if (csv) {
      csv.active = active;
    }
    _onRefresh?.();
  } catch (err) {
    console.error("[CF] CSV toggle failed:", err);
  }
}

async function toggleCsvMineAction(id: string, mineKeywords: boolean) {
  if (!_client) {
    return;
  }
  try {
    await _client.request("contentforge.csv.toggleMine", { id, mineKeywords });
    const csv = _state.csvSources.find((c) => c.id === id);
    if (csv) {
      (csv as Record<string, unknown>).mineKeywords = mineKeywords;
    }
    _onRefresh?.();
  } catch (err) {
    console.error("[CF] CSV mine toggle failed:", err);
  }
}

async function deleteCsvAction(id: string) {
  if (!_client) {
    return;
  }
  if (!confirm("Delete this CSV source and all its products?")) {
    return;
  }
  try {
    await _client.request("contentforge.csv.delete", { id });
    _state.csvSources = _state.csvSources.filter((c) => c.id !== id);
    _onRefresh?.();
  } catch (err) {
    console.error("[CF] CSV delete failed:", err);
  }
}

async function loadWebSources() {
  if (!_client || _state.webSourcesLoaded) {
    return;
  }
  try {
    const siteId = _state.site?.id;
    const res = await _client.request<{ sources: SettingsState["webSources"] }>(
      "contentforge.websources.list",
      { siteId: siteId ?? undefined },
    );
    _state.webSources = res?.sources ?? [];
    _state.webSourcesLoaded = true;
  } catch (err) {
    console.warn("[CF] Failed to load web sources:", err);
  }
  _onRefresh?.();
}

async function addWebSourceAction() {
  const name = (document.getElementById("ws-name") as HTMLInputElement | null)?.value?.trim();
  const sitemapUrl = (
    document.getElementById("ws-sitemap") as HTMLInputElement | null
  )?.value?.trim();
  const awinMid = (document.getElementById("ws-awinmid") as HTMLInputElement | null)?.value?.trim();
  const awinAffId = (
    document.getElementById("ws-awinaffid") as HTMLInputElement | null
  )?.value?.trim();
  const siteId = _state.site?.id;

  if (!name || !sitemapUrl || !awinMid || !awinAffId || !siteId || !_client) {
    return;
  }

  _state.addingWebSource = true;
  _onRefresh?.();

  try {
    await _client.request("contentforge.websources.add", {
      siteId,
      name,
      sitemapUrl,
      awinMid,
      awinAffId,
    });
    _state.webSourcesLoaded = false;
    await loadWebSources();
  } catch (err) {
    console.error("[CF] Failed to add web source:", err);
  }

  _state.addingWebSource = false;
  _onRefresh?.();
}

async function removeWebSourceAction(id: string) {
  if (!_client) {
    return;
  }
  try {
    await _client.request("contentforge.websources.remove", { id });
    _state.webSources = _state.webSources.filter((w) => w.id !== id);
    _onRefresh?.();
  } catch (err) {
    console.error("[CF] Failed to remove web source:", err);
  }
}

async function toggleWebSourceAction(id: string, active: boolean) {
  if (!_client) {
    return;
  }
  try {
    await _client.request("contentforge.websources.toggle", { id, active });
    const ws = _state.webSources.find((w) => w.id === id);
    if (ws) {
      ws.active = active;
    }
    _onRefresh?.();
  } catch (err) {
    console.error("[CF] Failed to toggle web source:", err);
  }
}

async function toggleWebSourceMineAction(id: string, mineKeywords: boolean) {
  if (!_client) {
    return;
  }
  try {
    await _client.request("contentforge.websources.toggleMine", { id, mineKeywords });
    const ws = _state.webSources.find((w) => w.id === id);
    if (ws) {
      (ws as Record<string, unknown>).mineKeywords = mineKeywords;
    }
    _onRefresh?.();
  } catch (err) {
    console.error("[CF] Web source mine toggle failed:", err);
  }
}

async function scrapeWebSourceAction(id: string) {
  if (!_client) {
    return;
  }
  _state.scrapingSourceId = id;
  _onRefresh?.();

  try {
    const res = await _client.request<{ productCount: number; scrapedAt: number }>(
      "contentforge.websources.scrape",
      { id },
    );
    const ws = _state.webSources.find((w) => w.id === id);
    if (ws && res) {
      ws.productCount = res.productCount;
      ws.lastScrapedAt = res.scrapedAt;
    }
  } catch (err) {
    console.error("[CF] Scrape failed:", err);
  }

  _state.scrapingSourceId = null;
  _onRefresh?.();
}

async function runMinerAction() {
  if (!_client || !_state.site || _state.miningInProgress) {
    return;
  }

  _state.miningInProgress = true;
  _state.minerResult = null;
  _onRefresh?.();

  try {
    const res = await _client.request<{
      phase: string;
      totalGenerated: number;
      totalAfterCluster: number;
      totalAfterDedup: number;
      gscEasyWins: number;
      totalAdded: number;
      durationMs: number;
      allProductsMined?: boolean;
    }>("contentforge.miner.run", { siteId: _state.site.id });

    _state.minerResult = {
      phase: res.phase,
      totalGenerated: res.totalGenerated,
      totalAfterCluster: res.totalAfterCluster,
      totalAdded: res.totalAdded,
      gscEasyWins: res.gscEasyWins,
      durationMs: res.durationMs,
      allProductsMined: res.allProductsMined,
    };
  } catch (err) {
    console.error("[CF] Miner failed:", err);
  }

  _state.miningInProgress = false;
  _onRefresh?.();
}

/**
 * Run the Miner targeting a specific Amazon category only.
 * Does not affect the main miner flow — Phase D is targeted to one category.
 */
async function runMinerForCategory(categoryNodeId: string, categoryLabel: string) {
  if (!_client || !_state.site || _state.miningCategoryNodeId) {
    return;
  }

  _state.miningCategoryNodeId = categoryNodeId;
  _state.categoryMinerResult = null;
  _onRefresh?.();

  try {
    const res = await _client.request<{
      phase: string;
      totalGenerated: number;
      totalAfterCluster: number;
      totalAfterDedup: number;
      gscEasyWins: number;
      totalAdded: number;
      durationMs: number;
      allProductsMined?: boolean;
    }>("contentforge.miner.run", {
      siteId: _state.site.id,
      amazonCategoryNodeId: categoryNodeId,
    });

    _state.categoryMinerResult = {
      categoryLabel,
      totalGenerated: res.totalGenerated,
      totalAdded: res.totalAdded,
      durationMs: res.durationMs,
    };
  } catch (err) {
    console.error(`[CF] Miner for category ${categoryLabel} failed:`, err);
  }

  _state.miningCategoryNodeId = null;
  _onRefresh?.();
}

function renderProductSourcesSection(): TemplateResult {
  // Lazy load web sources on first render
  if (!_state.csvSourcesLoaded) {
    void loadCsvSources();
  }
  if (!_state.webSourcesLoaded) {
    void loadWebSources();
  }

  return html`
    <div class="cf-settings__section">
      <h2>📦 Product Sources</h2>
      <p class="cf-settings__desc">Manage product data sources for this project. Web sources crawl sitemaps and scrape product pages with Awin affiliate links.</p>

      <div style="margin-bottom: 20px; padding: 16px; border: 1px solid var(--cf-border); border-radius: 8px; background: var(--cf-card-bg);">
        <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 8px;">
          <h3 style="margin: 0; font-size: 14px;">⛏️ Keyword Miner</h3>
          <button
            class="cf-settings__btn cf-settings__btn--primary"
            @click=${runMinerAction}
            ?disabled=${_state.miningInProgress}
            style="font-size: 13px; padding: 6px 16px;"
          >${_state.miningInProgress ? "⏳ Mining..." : "⛏️ Run Miner"}</button>
        </div>
        <p style="color: var(--cf-text-muted); font-size: 12px; margin: 0;">Generate keyword ideas from all products using AI, validate with Google search data, and add to keyword queue.</p>
        ${
          _state.minerResult
            ? html`
          <div style="margin-top: 12px; padding: 12px; background: rgba(34, 197, 94, 0.1); border-radius: 6px; font-size: 13px;">
            ✅ <strong>${_state.minerResult.totalAdded} keywords added</strong>
            (Phase ${_state.minerResult.phase}: ${_state.minerResult.totalGenerated} ideas → ${_state.minerResult.totalAfterCluster} topics, ${_state.minerResult.gscEasyWins} GSC wins)
            in ${Math.round(_state.minerResult.durationMs / 1000)}s
            ${
              _state.minerResult.allProductsMined
                ? html`
                    <br /><em style="color: var(--cf-text-muted)"
                      >All products mined — next run uses GSC/Bing/Planner discovery</em
                    >
                  `
                : ""
            }
          </div>
        `
            : ""
        }
      </div>

      <div style="margin: 16px 0; padding: 16px; border: 1px solid var(--cf-border); border-radius: 8px; background: var(--cf-card-bg);">
        <div class="cf-settings__field" style="margin-bottom: 0;">
          <label for="cfg-miner-exclude" style="font-size: 13px; font-weight: 600;">🚫 Miner Exclude Terms</label>
          <input
            id="cfg-miner-exclude"
            type="text"
            .value=${_state.site?.minerExcludeTerms ?? ""}
            placeholder="pampers, sex, prezervative, lenjerie"
            style="width: 100%;"
          />
          <p style="color: var(--cf-text-muted); font-size: 11px; margin: 4px 0 0;">Comma-separated terms. Products with matching title, category, or description will be skipped by the Miner.</p>
        </div>
      </div>

      <div style="margin: 16px 0; padding: 16px; border: 1px solid var(--cf-border); border-radius: 8px; background: var(--cf-card-bg);">
        <div class="cf-settings__field" style="margin-bottom: 0;">
          <label for="cfg-site-niche" style="font-size: 13px; font-weight: 600;">🎯 Site Niche</label>
          <input
            id="cfg-site-niche"
            type="text"
            .value=${_state.site?.siteNiche ?? ""}
            placeholder="automotive window tinting and car accessories"
            style="width: 100%;"
          />
          <p style="color: var(--cf-text-muted); font-size: 11px; margin: 4px 0 0;">Describe your site's topic. The miner will only generate keywords relevant to this niche. Leave empty for no filtering.</p>
        </div>
      </div>

      <div style="margin: 16px 0; padding: 16px; border: 1px solid var(--cf-border); border-radius: 8px; background: var(--cf-card-bg);">
        <label style="display: flex; align-items: center; gap: 10px; cursor: pointer;">
          <input id="cfg-ai-quality-wall" type="checkbox" .checked=${_state.site?.aiQualityWall ?? false} />
          <div>
            <span style="font-size: 13px; font-weight: 600;">🛡️ AI Quality Wall</span>
            <p style="color: var(--cf-text-muted); font-size: 11px; margin: 2px 0 0;">GPT reviews keywords after clustering and removes garbage. Adds ~5s to Miner run.</p>
          </div>
        </label>
      </div>

      <div style="display: flex; align-items: center; justify-content: space-between; margin: 20px 0 12px;">
        <h3 style="margin: 0; font-size: 14px;">📄 CSV Sources</h3>
        <button
          class="cf-settings__btn cf-settings__btn--secondary"
          @click=${uploadCsvAction}
          style="font-size: 12px; padding: 4px 12px;"
        >+ Upload CSV</button>
      </div>

      ${
        _state.csvSources.length > 0
          ? _state.csvSources.map(
              (csv) => html`
          <div class="cf-source-card ${!csv.active ? "cf-source-card--inactive" : ""}">
            <div class="cf-source-card__header">
              <div class="cf-source-card__name">📄 ${csv.filename}</div>
              <div class="cf-source-card__actions">
                <span style="font-size: 12px; color: var(--cf-text-muted);">${csv.productCount} products</span>
                <button
                  class="cf-settings__btn cf-settings__btn--ghost"
                  @click=${() => toggleCsvAction(csv.id, !csv.active)}
                  title="${csv.active ? "Disable source" : "Enable source"}"
                  style="font-size: 12px; padding: 2px 8px;"
                >${csv.active ? "🟢" : "⚪"}</button>
                <button
                  class="cf-settings__btn cf-settings__btn--ghost"
                  @click=${() => toggleCsvMineAction(csv.id, csv.mineKeywords === false)}
                  title="${csv.mineKeywords !== false ? "Disable keyword mining from this source" : "Enable keyword mining from this source"}"
                  style="font-size: 12px; padding: 2px 8px;"
                >${csv.mineKeywords !== false ? "⛏️" : "🚫"}</button>
                <button
                  class="cf-settings__btn cf-settings__btn--ghost cf-settings__btn--danger"
                  @click=${() => deleteCsvAction(csv.id)}
                  title="Remove CSV source"
                  style="font-size: 12px; padding: 2px 8px;"
                >🗑️</button>
              </div>
            </div>
            <div style="display: flex; gap: 8px; padding: 8px 0 4px; flex-wrap: wrap;">
              <input
                id="csv-aff-${csv.id}"
                type="text"
                .value=${csv.affCode ?? ""}
                placeholder="aff_code"
                style="flex: 1; min-width: 100px; font-size: 12px; padding: 4px 8px;"
                title="2Performant affiliate code"
              />
              <input
                id="csv-mid-${csv.id}"
                type="text"
                .value=${csv.merchantId ?? ""}
                placeholder="unique (merchant ID)"
                style="flex: 1; min-width: 100px; font-size: 12px; padding: 4px 8px;"
                title="2Performant merchant/unique ID"
              />
              <input
                id="csv-st-${csv.id}"
                type="text"
                .value=${csv.trackingRef ?? ""}
                placeholder="st (tracking ref)"
                style="flex: 1; min-width: 100px; font-size: 12px; padding: 4px 8px;"
                title="2Performant tracking reference"
              />
              <button
                type="button"
                class="cf-settings__btn cf-settings__btn--ghost"
                style="font-size: 11px; padding: 2px 8px;"
                data-save-csv="${csv.id}"
                @click=${() => saveCsvAffiliateParams(csv.id)}
                title="Save affiliate params for this source"
              >💾</button>
            </div>
          </div>
        `,
            )
          : html`
              <div style="color: var(--cf-text-muted); padding: 8px 0; font-size: 13px">
                No CSV sources uploaded yet. Click "+ Upload CSV" above.
              </div>
            `
      }

      <h3 style="margin: 20px 0 12px; font-size: 14px;">🌐 Web Sources</h3>

      ${
        _state.webSources.length === 0 && _state.webSourcesLoaded
          ? html`
              <div style="color: var(--cf-text-muted); padding: 16px 0; font-size: 13px">
                No web sources configured yet. Add one below.
              </div>
            `
          : nothing
      }

      ${_state.webSources.map(
        (ws) => html`
        <div class="cf-source-card ${!ws.active ? "cf-source-card--inactive" : ""}">
          <div class="cf-source-card__header">
            <div class="cf-source-card__name">🌐 ${ws.name}</div>
            <div class="cf-source-card__actions">
              <button
                class="cf-settings__btn cf-settings__btn--ghost"
                @click=${() => toggleWebSourceAction(ws.id, !ws.active)}
                title="${ws.active ? "Disable source" : "Enable source"}"
              >${ws.active ? "🟢" : "⚪"}</button>
              <button
                class="cf-settings__btn cf-settings__btn--ghost"
                @click=${() => toggleWebSourceMineAction(ws.id, (ws as Record<string, unknown>).mineKeywords === false)}
                title="${(ws as Record<string, unknown>).mineKeywords !== false ? "Disable keyword mining" : "Enable keyword mining"}"
              >${(ws as Record<string, unknown>).mineKeywords !== false ? "⛏️" : "🚫"}</button>
              <button
                class="cf-settings__btn cf-settings__btn--ghost cf-settings__btn--danger"
                @click=${() => removeWebSourceAction(ws.id)}
                title="Remove"
              >🗑️</button>
            </div>
          </div>
          <div class="cf-source-card__details">
            <div><strong>Sitemap:</strong> <a href="${ws.sitemapUrl}" target="_blank" rel="noopener">${ws.sitemapUrl}</a></div>
            <div><strong>Awin MID:</strong> ${ws.awinMid} · <strong>Aff ID:</strong> ${ws.awinAffId}</div>
            <div><strong>Products:</strong> ${ws.productCount} · <strong>Last scraped:</strong> ${ws.lastScrapedAt ? new Date(ws.lastScrapedAt).toLocaleString() : "Never"}</div>
          </div>
          <div class="cf-source-card__footer">
            <button
              class="cf-settings__btn cf-settings__btn--secondary"
              @click=${() => scrapeWebSourceAction(ws.id)}
              ?disabled=${_state.scrapingSourceId === ws.id}
            >${_state.scrapingSourceId === ws.id ? "⏳ Scraping..." : "🔄 Scrape Now"}</button>
          </div>
        </div>
      `,
      )}

      <div class="cf-source-add">
        <h3>➕ Add Web Source</h3>
        <div class="cf-source-add__grid">
          <div class="cf-settings__field">
            <label>Source Name</label>
            <input id="ws-name" class="cf-settings__input" placeholder="e.g. Ottocast Products" />
          </div>
          <div class="cf-settings__field">
            <label>Product Sitemap URL</label>
            <input id="ws-sitemap" class="cf-settings__input" placeholder="https://example.com/sitemap_products.xml" />
          </div>
          <div class="cf-settings__field">
            <label>Awin Merchant ID (awinmid)</label>
            <input id="ws-awinmid" class="cf-settings__input" placeholder="96499" />
          </div>
          <div class="cf-settings__field">
            <label>Awin Affiliate ID (awinaffid)</label>
            <input id="ws-awinaffid" class="cf-settings__input" placeholder="2673596" />
          </div>
        </div>
        <button
          class="cf-settings__btn cf-settings__btn--primary"
          style="margin-top: 12px;"
          @click=${addWebSourceAction}
          ?disabled=${_state.addingWebSource}
        >${_state.addingWebSource ? "Adding..." : "➕ Add Source"}</button>
      </div>
    </div>
  `;
}

// ─── Amazon Section ──────────────────────────────────────────────────────

function renderAmazonSection(site: SiteData) {
  const amz = site.amazonSource;

  // No Amazon config yet — show setup button
  if (!amz) {
    return html`
      <div class="cf-settings__section">
        <h2 class="cf-settings__section-title">🛒 Amazon Integration</h2>
        <p class="cf-settings__section-desc">
          Connect Amazon Associates to discover products, generate buyer-intent keywords, and include
          affiliate product cards in articles.
        </p>
        <div class="cf-settings__field">
          <label for="cfg-amz-tag">Amazon Associate Tag</label>
          <input id="cfg-amz-tag" type="text" placeholder="your-tag-20" />
          <span class="cf-settings__hint">Your Amazon Associates tracking ID (e.g. tintfinder-20).</span>
        </div>
        <div class="cf-settings__field">
          <label for="cfg-amz-domain">Amazon Domain</label>
          <select id="cfg-amz-domain">
            <option value="amazon.com" selected>amazon.com (US)</option>
            <option value="amazon.co.uk">amazon.co.uk (UK)</option>
            <option value="amazon.de">amazon.de (Germany)</option>
            <option value="amazon.fr">amazon.fr (France)</option>
            <option value="amazon.it">amazon.it (Italy)</option>
            <option value="amazon.es">amazon.es (Spain)</option>
            <option value="amazon.ca">amazon.ca (Canada)</option>
            <option value="amazon.co.jp">amazon.co.jp (Japan)</option>
          </select>
          <span class="cf-settings__hint">Select the Amazon marketplace for your affiliate program.</span>
        </div>
        <p class="cf-settings__hint" style="margin-top: 12px">
          💡 Enter your Associate Tag and click "Save Settings" to enable Amazon integration.
        </p>
      </div>
    `;
  }

  // Amazon config exists — show full settings
  const usedCount = amz.usedAsins?.length ?? 0;
  const catCount = amz.categories?.length ?? 0;

  return html`
    <div class="cf-settings__section">
      <h2 class="cf-settings__section-title">🛒 Amazon Integration</h2>

      <div class="cf-settings__field">
        <label for="cfg-amz-tag">Associate Tag</label>
        <input
          id="cfg-amz-tag"
          type="text"
          .value=${amz.tag || ""}
          placeholder="your-tag-20"
        />
        <span class="cf-settings__hint">Your Amazon Associates tracking ID.</span>
      </div>

      <div class="cf-settings__field">
        <label for="cfg-amz-domain">Amazon Domain</label>
        <select id="cfg-amz-domain" .value=${amz.domain || "amazon.com"}>
          ${[
            "amazon.com",
            "amazon.co.uk",
            "amazon.de",
            "amazon.fr",
            "amazon.it",
            "amazon.es",
            "amazon.ca",
            "amazon.co.jp",
          ].map(
            (d) =>
              html`<option value=${d} ?selected=${(amz.domain || "amazon.com") === d}>${d}</option>`,
          )}
        </select>
      </div>

      <h2 class="cf-settings__section-title" style="margin-top: 24px;">📊 Product Filters</h2>

      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;">
        <div class="cf-settings__field">
          <label for="cfg-amz-min-price">Min Price ($)</label>
          <input
            id="cfg-amz-min-price"
            type="number"
            min="0"
            step="1"
            .value=${String(amz.minPrice ?? 30)}
          />
        </div>
        <div class="cf-settings__field">
          <label for="cfg-amz-min-rating">Min Rating (★)</label>
          <input
            id="cfg-amz-min-rating"
            type="number"
            min="0"
            max="5"
            step="0.1"
            .value=${String(amz.minRating ?? 4.0)}
          />
        </div>
        <div class="cf-settings__field">
          <label for="cfg-amz-min-reviews">Min Reviews</label>
          <input
            id="cfg-amz-min-reviews"
            type="number"
            min="0"
            step="1"
            .value=${String(amz.minReviews ?? 100)}
          />
        </div>
      </div>
      <span class="cf-settings__hint">Products below these thresholds are automatically filtered out.</span>

      <h2 class="cf-settings__section-title" style="margin-top: 24px;">🔧 Pipeline Controls</h2>

      <div class="cf-settings__field">
        <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
          <input type="checkbox" id="cfg-amz-miner" ?checked=${amz.minerEnabled !== false} />
          Enable Amazon Miner
        </label>
        <span class="cf-settings__hint">
          ${
            amz.minerEnabled !== false
              ? "Miner discovers buyer-intent keywords from Amazon Best Sellers & New Releases."
              : "Amazon product discovery is disabled. Only standard keyword mining runs."
          }
        </span>
      </div>

      <div class="cf-settings__field">
        <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
          <input type="checkbox" id="cfg-amz-sourcer" ?checked=${amz.sourcerEnabled !== false} />
          Enable Amazon Sourcer
        </label>
        <span class="cf-settings__hint">
          ${
            amz.sourcerEnabled !== false
              ? "Sourcer searches Amazon for relevant products to include in articles."
              : "Amazon sourcing is disabled. Products come from CSV/Web sources only."
          }
        </span>
      </div>

      <h2 class="cf-settings__section-title" style="margin-top: 24px;">📁 Categories (${catCount})</h2>
      ${
        catCount === 0
          ? html`
              <p class="cf-settings__hint">
                No categories configured. Add Amazon subcategory node IDs for product discovery.
              </p>
            `
          : html`
            <div style="display: flex; flex-direction: column; gap: 6px;">
              ${(amz.categories || []).map(
                (c, i) => html`
                  <div style="display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: rgba(255,255,255,0.05); border-radius: 6px;">
                    <span style="flex: 1; font-size: 13px;">
                      <strong>${c.label}</strong>
                      <span style="opacity: 0.5; margin-left: 8px;">node: ${c.nodeId}</span>
                    </span>
                    <button
                      class="cf-settings__btn cf-settings__btn--primary"
                      style="padding: 2px 10px; font-size: 11px;"
                      title="Run Miner for this category"
                      ?disabled=${_state.miningCategoryNodeId === c.nodeId}
                      @click=${(e: Event) => {
                        e.preventDefault();
                        void runMinerForCategory(c.nodeId, c.label);
                      }}
                    >${_state.miningCategoryNodeId === c.nodeId ? "⏳" : "⛏️"}</button>
                    <button
                      class="cf-settings__btn cf-settings__btn--secondary"
                      style="padding: 2px 8px; font-size: 11px;"
                      @click=${() => removeCategory(i)}
                    >✕</button>
                  </div>
                `,
              )}
            </div>
          `
      }

      ${
        _state.categoryMinerResult
          ? html`
          <div style="margin-top: 12px; padding: 12px; background: rgba(34, 197, 94, 0.1); border-radius: 6px; font-size: 13px;">
            ✅ <strong>${_state.categoryMinerResult.categoryLabel}</strong>:
            ${_state.categoryMinerResult.totalAdded} keywords added
            (${_state.categoryMinerResult.totalGenerated} ideas)
            in ${Math.round(_state.categoryMinerResult.durationMs / 1000)}s
          </div>
        `
          : ""
      }

      <div style="display: flex; gap: 8px; margin-top: 10px;">
        <input id="cfg-amz-cat-label" type="text" placeholder="Category label (e.g. Dashcams)" style="flex: 1;" />
        <input id="cfg-amz-cat-node" type="text" placeholder="Paste Amazon URL or Node ID" style="flex: 2;" />
        <button
          class="cf-settings__btn cf-settings__btn--secondary"
          @click=${addCategory}
        >+ Add</button>
      </div>

      <h2 class="cf-settings__section-title" style="margin-top: 24px;">📦 Used ASINs</h2>
      <div style="display: flex; align-items: center; gap: 12px;">
        <span style="font-size: 14px;">${usedCount} ASINs used so far</span>
        ${
          usedCount > 0
            ? html`
              <button
                class="cf-settings__btn cf-settings__btn--secondary"
                style="font-size: 12px; padding: 4px 12px;"
                @click=${resetUsedAsins}
              >🔄 Reset</button>
            `
            : nothing
        }
      </div>
      <span class="cf-settings__hint">Used ASINs are skipped during product discovery to avoid repeats.</span>

      <!-- Article Product Refresh -->
      <h2 class="cf-settings__section-title" style="margin-top: 32px;">🔄 Article Product Refresh</h2>
      <span class="cf-settings__hint" style="display: block; margin-bottom: 12px;">
        Re-scrape Amazon for new products, replace all product cards, comparison table, and rewrite body paragraphs.
      </span>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;">
        <div>
          <label style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; margin-bottom: 4px; display: block;">Article URL</label>
          <input
            id="cfg-refresh-url"
            type="url"
            placeholder="https://${_state.site?.url?.replace(/^https?:\/\//, "") || "example.com"}/article-slug..."
            style="width: 100%;"
          />
        </div>
        <div>
          <label style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; margin-bottom: 4px; display: block;">Amazon Search Keywords</label>
          <input
            id="cfg-refresh-keywords"
            type="text"
            placeholder="double din head unit"
            style="width: 100%;"
          />
        </div>
      </div>
      <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
        <button
          class="cf-settings__btn cf-settings__btn--primary"
          style="background: linear-gradient(135deg, #FF9900, #e88500); border: none; color: #000; font-weight: 700; padding: 8px 20px;"
          ?disabled=${_articleRefreshing}
          @click=${async () => {
            const urlInput = document.getElementById("cfg-refresh-url") as HTMLInputElement;
            const kwInput = document.getElementById("cfg-refresh-keywords") as HTMLInputElement;
            const url = urlInput?.value?.trim();
            const kw = kwInput?.value?.trim();
            if (!url) {
              _articleRefreshError = "Enter an article URL";
              _onRefresh?.();
              return;
            }
            if (!kw) {
              _articleRefreshError = "Enter search keywords";
              _onRefresh?.();
              return;
            }
            _articleRefreshing = true;
            _articleRefreshResult = null;
            _articleRefreshError = null;
            _onRefresh?.();
            try {
              const result = await _client!.request<{
                success: boolean;
                oldProducts: string[];
                newProducts: string[];
                cardsReplaced: number;
                tableReplaced: boolean;
                paragraphsRewritten: number;
                details: string[];
              }>("contentforge.article.refresh", {
                siteId: _state.site?.id || "default",
                articleUrl: url,
                keywords: kw,
              });
              _articleRefreshResult = result ?? null;
            } catch (err) {
              _articleRefreshError = String(err);
            }
            _articleRefreshing = false;
            _onRefresh?.();
          }}
        >
          ${_articleRefreshing ? "⏳ Refreshing..." : "🔄 Refresh Products"}
        </button>
        ${
          _articleRefreshError
            ? html`<span style="color: #f87171; font-size: 0.85rem;">❌ ${_articleRefreshError}</span>`
            : nothing
        }
      </div>

      ${
        _articleRefreshResult
          ? html`
          <div style="margin-top: 16px; padding: 14px; border-radius: 10px; background: ${_articleRefreshResult.success ? "rgba(34, 197, 94, 0.08)" : "rgba(248, 113, 113, 0.08)"}; border: 1px solid ${_articleRefreshResult.success ? "rgba(34, 197, 94, 0.25)" : "rgba(248, 113, 113, 0.25)"};">
            <div style="font-weight: 600; margin-bottom: 8px; color: ${_articleRefreshResult.success ? "#22c55e" : "#f87171"};">
              ${_articleRefreshResult.success ? "✅ Products refreshed successfully!" : "❌ Refresh failed"}
            </div>
            ${
              _articleRefreshResult.oldProducts?.length > 0
                ? html`<div style="font-size: 0.85rem; margin-bottom: 4px;"><strong>Old:</strong> ${_articleRefreshResult.oldProducts.join(", ")}</div>`
                : nothing
            }
            ${
              _articleRefreshResult.newProducts?.length > 0
                ? html`<div style="font-size: 0.85rem; margin-bottom: 4px;"><strong>New:</strong> ${_articleRefreshResult.newProducts.join(", ")}</div>`
                : nothing
            }
            <div style="font-size: 0.8rem; opacity: 0.7; margin-top: 8px;">
              ${(_articleRefreshResult.details ?? []).map(
                (d: string) => html`<div style="margin: 2px 0;">${d}</div>`,
              )}
            </div>
          </div>
        `
          : nothing
      }
      <!-- Fix Broken Products -->
      <h2 class="cf-settings__section-title" style="margin-top: 32px;">🔧 Fix Broken Product Cards</h2>
      <span class="cf-settings__hint" style="display: block; margin-bottom: 12px;">
        Re-scrape Amazon for missing images, ratings, and reviews. Fetches live content from DB,
        patches the HTML, and pushes via PUT. Leave URLs empty to scan all articles.
      </span>
      <div style="margin-bottom: 12px;">
        <label style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; margin-bottom: 4px; display: block;">Article URLs (one per line, optional)</label>
        <textarea
          id="cfg-fix-urls"
          rows="4"
          placeholder="https://${_state.site?.url?.replace(/^https?:\/\//, "") || "example.com"}/article-slug/&#10;https://..."
          style="width: 100%; font-size: 13px; font-family: monospace;"
        ></textarea>
      </div>
      <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
        <button
          class="cf-settings__btn cf-settings__btn--primary"
          style="background: linear-gradient(135deg, #f59e0b, #d97706); border: none; color: #000; font-weight: 700; padding: 8px 20px;"
          ?disabled=${_fixBrokenRunning}
          @click=${async () => {
            const urlsTextarea = document.getElementById("cfg-fix-urls") as HTMLTextAreaElement;
            const rawUrls = urlsTextarea?.value?.trim();
            const urls = rawUrls
              ? rawUrls
                  .split("\n")
                  .map((u: string) => u.trim())
                  .filter((u: string) => u.length > 0)
              : undefined;

            _fixBrokenRunning = true;
            _fixBrokenResult = null;
            _fixBrokenError = null;
            _onRefresh?.();
            try {
              const result = await _client!.request<{
                fixed: number;
                skipped: number;
                errors: number;
                details: string[];
              }>("contentforge.repair.fixBrokenProducts", {
                siteId: _state.site?.id || "default",
                ...(urls ? { urls } : {}),
              });
              _fixBrokenResult = result ?? null;
            } catch (err) {
              _fixBrokenError = String(err);
            }
            _fixBrokenRunning = false;
            _onRefresh?.();
          }}
        >
          ${_fixBrokenRunning ? "⏳ Fixing..." : "🔧 Fix Broken Products"}
        </button>
        ${
          _fixBrokenError
            ? html`<span style="color: #f87171; font-size: 0.85rem;">❌ ${_fixBrokenError}</span>`
            : nothing
        }
      </div>

      ${
        _fixBrokenResult
          ? html`
          <div style="margin-top: 16px; padding: 14px; border-radius: 10px; background: ${_fixBrokenResult.errors === 0 ? "rgba(34, 197, 94, 0.08)" : "rgba(248, 113, 113, 0.08)"}; border: 1px solid ${_fixBrokenResult.errors === 0 ? "rgba(34, 197, 94, 0.25)" : "rgba(248, 113, 113, 0.25)"}; ">
            <div style="font-weight: 600; margin-bottom: 8px; color: ${_fixBrokenResult.errors === 0 ? "#22c55e" : "#f87171"};">
              ${_fixBrokenResult.fixed > 0 ? "✅" : "ℹ️"} ${_fixBrokenResult.fixed} article(s) fixed, ${_fixBrokenResult.skipped} skipped, ${_fixBrokenResult.errors} errors
            </div>
            <div style="font-size: 0.8rem; opacity: 0.7; margin-top: 8px;">
              ${(_fixBrokenResult.details ?? []).map(
                (d: string) => html`<div style="margin: 2px 0;">${d}</div>`,
              )}
            </div>
          </div>
        `
          : nothing
      }
    </div>
  `;
}

function addCategory() {
  const labelEl = document.getElementById("cfg-amz-cat-label") as HTMLInputElement | null;
  const nodeEl = document.getElementById("cfg-amz-cat-node") as HTMLInputElement | null;
  if (!labelEl?.value || !nodeEl?.value) {
    return;
  }

  // Parse node ID from input — supports:
  // 1. Plain node ID: "3248684011"
  // 2. Full Amazon URL with rh=n:XXXX format (takes last n: value = most specific)
  // 3. URL with node=XXXX parameter
  let nodeId = nodeEl.value.trim();
  if (nodeId.includes("amazon.")) {
    // Try rh=n:XXXX format (URL-decoded: n:123,n:456 or URL-encoded: n%3A123%2Cn%3A456)
    const decoded = decodeURIComponent(nodeId);
    const rhMatch = decoded.match(/rh=[^&]*n:(\d+)(?:[^n]*n:(\d+))*/);
    if (rhMatch) {
      // Get all n:XXXX values and use the last one (most specific)
      const allNodes = [...decoded.matchAll(/n:(\d+)/g)].map((m) => m[1]);
      nodeId = allNodes[allNodes.length - 1] || nodeId;
    } else {
      // Try node=XXXX format
      const nodeMatch = decoded.match(/node=(\d+)/);
      if (nodeMatch) {
        nodeId = nodeMatch[1];
      }
    }
  }

  if (_state.site?.amazonSource) {
    _state.site.amazonSource.categories = [
      ...(_state.site.amazonSource.categories || []),
      { label: labelEl.value.trim(), nodeId },
    ];
    labelEl.value = "";
    nodeEl.value = "";
    _onRefresh?.();
  }
}

function removeCategory(index: number) {
  if (_state.site?.amazonSource) {
    _state.site.amazonSource.categories = _state.site.amazonSource.categories.filter(
      (_, i) => i !== index,
    );
    _onRefresh?.();
  }
}

async function resetUsedAsins() {
  if (!_client || !_state.site?.id || !_state.site?.amazonSource) {
    return;
  }
  _state.site.amazonSource.usedAsins = [];
  try {
    await _client.request("contentforge.sites.update", {
      id: _state.site.id,
      patch: {
        amazonSource: { ..._state.site.amazonSource, usedAsins: [] },
      },
    });
  } catch (err) {
    console.error("Failed to reset ASINs:", err);
  }
  _onRefresh?.();
}

// ─── Reset on view change ───────────────────────────────────────────────

export function resetSettingsState() {
  _initialized = false;
  _state = {
    site: null,
    defaults: null,
    loading: true,
    saving: false,
    saved: false,
    error: null,
    wpTestResult: null,
    wpTesting: false,
    activeSection: "site",
    csvSources: [],
    csvSourcesLoaded: false,
    webSources: [],
    webSourcesLoaded: false,
    addingWebSource: false,
    scrapingSourceId: null,
    miningInProgress: false,
    minerResult: null,
    miningCategoryNodeId: null,
    categoryMinerResult: null,
  };
  _articleRefreshing = false;
  _articleRefreshResult = null;
  _articleRefreshError = null;
}
